import { Component, computed, Renderer2, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from '@/layout/components/app.breadcrumb';
import { AppFooter } from '@/layout/components/app.footer';
import { AppSearch } from '@/layout/components/app.search';
import { ShortcutComponent } from '@/pages/Components/shortcut/shortcut.component';
import { isHomeRoute } from '@/layout/route-context';
// import { InactivityWarningComponent } from '@/util-components/inactivity-warning/inactivity-warning.component';
// import { InactivityService } from '@/services/inactivity.service';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, AppBreadcrumb, AppFooter, AppSearch, ShortcutComponent],
    template: `
        <div class="layout-wrapper" [ngClass]="containerClass()">
            <!-- Row 1 (top bar) + row 2 (menu bar) both live inside app-sidebar;
                 see the grid in _sidebar_horizontal.scss. -->
            <div app-sidebar></div>
            <div class="layout-content-wrapper">
                <div class="layout-content-wrapper-inside">
                    <!-- Mobile-only top bar; hidden at desktop widths, where the
                         one inside app-sidebar is used instead. -->
                    <div app-topbar class="sticky-topbar" *ngIf="layoutService.isMobile()"></div>
                    <!-- Row 3: breadcrumb band, full width under the menu bar.
                         The dashboard is the root of every trail, so it gets no
                         band at all rather than an empty one. -->
                    <div class="layout-breadcrumb-bar" *ngIf="showBreadcrumbBar()">
                        <div app-breadcrumb></div>
                    </div>
                    <div class="layout-content">
                        <router-outlet></router-outlet>
                        <app-shortcut *ngIf="shouldShowShortcut()"></app-shortcut>
                    </div>
                    <div app-footer></div>
                </div>
            </div>
            <div app-search></div>
            <div class="layout-mask animate-fadein"></div>
            <!-- <app-inactivity-warning></app-inactivity-warning> -->
        </div>
    `
})
export class AppLayout implements OnInit {
    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    menuScrollListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        // private inactivityService: InactivityService
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => this.outsideClickListener(event));
            }
            if (this.layoutService.isHorizontal() && !this.menuScrollListener) {
                this.menuScrollListener = this.renderer.listen(this.appSidebar.menuContainer.nativeElement, 'scroll', (event) => {
                    if (this.layoutService.isDesktop()) {
                        this.hideMenu();
                    }
                });
            }
            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();

            const container = document.querySelector('.layout-content-wrapper');
            if (container) {
                (container as HTMLElement).scrollTop = 0;
            }
        });
    }

    ngOnInit() {
        // Start tracking user inactivity when layout is initialized
        // this.inactivityService.startTracking();
    }

    showBreadcrumbBar(): boolean {
        return !isHomeRoute(this.router);
    }

    shouldShowShortcut(): boolean {
        const url = this.router.url;
        return !url.includes('extraction') && !url.endsWith('home') && !url.includes('masterdata');
    }

    outsideClickListener(event: any) {
        if (this.isOutsideClicked(event)) {
            this.hideMenu();
        }
    }

    isOutsideClicked(event: any) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarButtonEl = document.querySelector('.topbar-left > a');

        return !(sidebarEl?.isSameNode(event.target) || sidebarEl?.contains(event.target) || topbarButtonEl?.isSameNode(event.target) || topbarButtonEl?.contains(event.target));
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({
            ...prev,
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            menuHoverActive: false
        }));
        if (this.layoutService.isHorizontal()) {
            this.layoutService.onMenuStateChange({ key: '' });
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }

        if (this.menuScrollListener) {
            this.menuScrollListener();
            this.menuScrollListener = null;
        }

        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    containerClass = computed(() => {
        const layoutState = this.layoutService.layoutState();

        return {
            // The layout is always horizontal; only mobile open/close varies.
            'layout-horizontal': true,
            'layout-overlay-active': layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
            'layout-mobile-active': layoutState.staticMenuMobileActive,
            'layout-sidebar-active': layoutState.sidebarActive,
            'layout-sidebar-anchored': layoutState.anchored
        };
    });

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }

        // Stop tracking inactivity when layout is destroyed
        // this.inactivityService.stopTracking();
    }
}
